import React from 'react';
import styled, { css } from 'styled-components';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';

/* ─────────────────────────── Types ───────────────────────────────── */

type NotificationStatus = 'seen' | 'unseen';
type NotificationPriority = 'info' | 'warning' | 'error' | 'success';
type NotificationVariant = 'default' | 'compact';

export interface NotificationItemProps {
  status?: NotificationStatus;
  /** Overrides dot color when provided — takes precedence over status */
  priority?: NotificationPriority;
  title: React.ReactNode;
  date?: string;
  /** Alternative to date — shows relative time string (e.g., "5 min ago") */
  relativeTime?: string;
  onTitleClick?: () => void;
  /** Render title as raw HTML (for notification messages with markup) */
  dangerousHtml?: string;
  /** Action slot — typically NotificationItem.Delete or NotificationItem.Actions */
  action?: React.ReactNode;
  /** Avatar slot — rendered before the title/date content block */
  avatar?: React.ReactNode;
  /** Unread badge count displayed near the dot indicator */
  badge?: number;
  /** Density variant */
  variant?: NotificationVariant;
  /** Grouped — shows group count label below date */
  grouped?: boolean;
  /** Number of grouped notifications (shown as label) */
  groupCount?: number;
  /** Show stacked card shadow effect (only applied when grouped is true) */
  stackEffect?: boolean;
  className?: string;
}

/* ─────────────────────────── Priority → Color ────────────────────── */

function getDotColor(status: NotificationStatus, priority?: NotificationPriority): string {
  if (priority) {
    switch (priority) {
      case 'info': return palette.Purple;
      case 'success': return palette.Green;
      case 'warning': return palette.amber[500];
      case 'error': return palette.Red;
    }
  }
  return status === 'seen' ? palette.Green : palette.Purple;
}

/* ─────────────────────────── Styled: Item ────────────────────────── */

interface StyledItemTransient {
  $dotColor: string;
  $variant: NotificationVariant;
  $stackEffect: boolean;
  $hasAvatar: boolean;
}

const StyledItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<StyledItemTransient>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: ${rem(1)} solid ${palette.CulturedWhite};

  ${({ $variant }) =>
    $variant === 'compact'
      ? css`padding: ${rem(10)} ${rem(24)};`
      : css`padding: ${rem(15)} ${rem(32)};`}

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '';
    position: absolute;
    width: 9px;
    height: 9px;
    border-radius: ${rem(2)};
    background-color: ${({ $dotColor }) => $dotColor};

    ${({ $variant }) =>
      $variant === 'compact'
        ? css`top: ${rem(17)}; left: ${rem(24)};`
        : css`top: ${rem(22)}; left: ${rem(32)};`}

    ${({ $hasAvatar }) =>
      $hasAvatar &&
      css`display: none;`}
  }

  ${({ $stackEffect }) =>
    $stackEffect &&
    css`
      box-shadow: 
        ${rem(2)} ${rem(2)} 0 0 ${palette.CulturedWhite},
        ${rem(4)} ${rem(4)} 0 0 ${palette.CulturedWhite};
      margin-bottom: ${rem(6)};
      border-radius: ${rem(4)};
      border: ${rem(1)} solid ${palette.CulturedWhite};
    `}
`;

/* ─────────────────────────── Styled: Content ─────────────────────── */

const StyledContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(10)};
  flex: 1;
  min-width: 0;
`;

const StyledTextBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

/* ─────────────────────────── Styled: Title ───────────────────────── */

interface StyledTitleTransient {
  $clickable: boolean;
  $variant: NotificationVariant;
  $hasAvatar: boolean;
}

const StyledTitle = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<StyledTitleTransient>`
  margin-bottom: ${rem(5)};
  padding-left: ${({ $hasAvatar }) => $hasAvatar ? '0' : rem(14)};

  ${({ $variant }) =>
    $variant === 'compact'
      ? css`font-size: ${rem(14)}; line-height: ${rem(20)};`
      : css`font-size: ${rem(16)}; line-height: ${rem(22)};`}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    `}
`;

/* ─────────────────────────── Styled: Date ────────────────────────── */

const StyledDate = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $variant: NotificationVariant; $hasAvatar: boolean }>`
  color: ${palette.SpanishGray};
  padding-left: ${({ $hasAvatar }) => $hasAvatar ? '0' : rem(14)};

  ${({ $variant }) =>
    $variant === 'compact'
      ? css`font-size: ${rem(11)};`
      : css`font-size: ${rem(12)};`}
`;

/* ─────────────────────────── Styled: Badge ───────────────────────── */

const StyledBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${palette.Red};
  color: ${palette.White};
  font-size: ${rem(10)};
  font-weight: 700;
  line-height: 1;
  padding: ${rem(2)} ${rem(5)};
  border-radius: ${rem(10)};
  min-width: ${rem(16)};
  margin-left: ${rem(6)};
  vertical-align: middle;
`;

/* ─────────────────────────── Styled: Group Label ─────────────────── */

const StyledGroupLabel = styled.div`
  padding-left: ${rem(14)};
  font-size: ${rem(11)};
  color: ${palette.SpanishGray};
  margin-top: ${rem(2)};
`;

/* ─────────────────────────── Sub: Delete ─────────────────────────── */

interface DeleteProps {
  onClick?: () => void;
  loading?: boolean;
}

const StyledActionIcon = styled.span.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $color?: string }>`
  color: ${({ $color }) => $color ?? palette.Red};
  font-size: ${rem(20)};
  cursor: pointer;
  margin-top: ${rem(2.5)};
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 48;
`;

function NotificationItemDelete({ onClick, loading }: DeleteProps): JSX.Element {
  if (loading) {
    return <StyledActionIcon $color={palette.Red}>...</StyledActionIcon>;
  }
  return (
    <StyledActionIcon $color={palette.Red} className='material-symbols-sharp' onClick={onClick}>
      Delete
    </StyledActionIcon>
  );
}
NotificationItemDelete.displayName = 'NotificationItem.Delete';

/* ─────────────────────────── Sub: MarkRead ───────────────────────── */

interface MarkReadProps {
  onClick?: () => void;
}

function NotificationItemMarkRead({ onClick }: MarkReadProps): JSX.Element {
  return (
    <StyledActionIcon $color={palette.Green} className='material-symbols-sharp' onClick={onClick}>
      done
    </StyledActionIcon>
  );
}
NotificationItemMarkRead.displayName = 'NotificationItem.MarkRead';

/* ─────────────────────────── Sub: Pin ────────────────────────────── */

interface PinProps {
  onClick?: () => void;
  active?: boolean;
}

function NotificationItemPin({ onClick, active }: PinProps): JSX.Element {
  return (
    <StyledActionIcon
      $color={active ? palette.Purple : palette.SpanishGray}
      className='material-symbols-sharp'
      onClick={onClick}
    >
      push_pin
    </StyledActionIcon>
  );
}
NotificationItemPin.displayName = 'NotificationItem.Pin';

/* ─────────────────────────── Sub: Archive ────────────────────────── */

interface ArchiveProps {
  onClick?: () => void;
}

function NotificationItemArchive({ onClick }: ArchiveProps): JSX.Element {
  return (
    <StyledActionIcon $color={palette.SpanishGray} className='material-symbols-sharp' onClick={onClick}>
      archive
    </StyledActionIcon>
  );
}
NotificationItemArchive.displayName = 'NotificationItem.Archive';

/* ─────────────────────────── Sub: Avatar ─────────────────────────── */

interface AvatarProps {
  src?: string;
  name?: string;
  size?: number;
}

const StyledAvatar = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $size: number }>`
  width: ${({ $size }) => rem($size)};
  height: ${({ $size }) => rem($size)};
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${palette.Purple};
  color: ${palette.White};
  font-size: ${({ $size }) => rem($size * 0.4)};
  font-weight: 600;
`;

const StyledAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

function getInitials(name?: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function NotificationItemAvatar({ src, name, size = 32 }: AvatarProps): JSX.Element {
  return (
    <StyledAvatar $size={size}>
      {src ? (
        <StyledAvatarImg src={src} alt={name ?? 'avatar'} />
      ) : (
        <span>{getInitials(name)}</span>
      )}
    </StyledAvatar>
  );
}
NotificationItemAvatar.displayName = 'NotificationItem.Avatar';

/* ─────────────────────────── Sub: Actions ────────────────────────── */

interface ActionsProps {
  children: React.ReactNode;
}

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${rem(8)};
`;

function NotificationItemActions({ children }: ActionsProps): JSX.Element {
  return <StyledActions>{children}</StyledActions>;
}
NotificationItemActions.displayName = 'NotificationItem.Actions';

/* ─────────────────────────── Root Component ──────────────────────── */

function NotificationItemRoot({
  status = 'unseen',
  priority,
  title,
  date,
  relativeTime,
  onTitleClick,
  action,
  avatar,
  badge,
  variant = 'default',
  grouped = false,
  groupCount,
  stackEffect = false,
  className,
  dangerousHtml,
}: NotificationItemProps): JSX.Element {
  const dotColor = getDotColor(status, priority);
  const displayDate = relativeTime ?? date;
  const hasAvatar = !!avatar;
  const badgeEl = badge != null && badge > 0
    ? <StyledBadge>{badge > 99 ? '99+' : badge}</StyledBadge>
    : null;

  return (
    <StyledItem $dotColor={dotColor} $variant={variant} $stackEffect={grouped && stackEffect} $hasAvatar={hasAvatar} className={className}>
      <StyledContentWrapper>
        {avatar}
        <StyledTextBlock>
          {dangerousHtml ? (
            <StyledTitle
              $clickable={!!onTitleClick}
              $variant={variant}
              $hasAvatar={hasAvatar}
              onClick={onTitleClick}
            >
              <span dangerouslySetInnerHTML={{ __html: dangerousHtml }} />
              {badgeEl}
            </StyledTitle>
          ) : (
            <StyledTitle $clickable={!!onTitleClick} $variant={variant} $hasAvatar={hasAvatar} onClick={onTitleClick}>
              {title}
              {badgeEl}
            </StyledTitle>
          )}
          {displayDate && <StyledDate $variant={variant} $hasAvatar={hasAvatar}>{displayDate}</StyledDate>}
          {grouped && groupCount && groupCount > 1 && (
            <StyledGroupLabel>{groupCount} similar notifications</StyledGroupLabel>
          )}
        </StyledTextBlock>
      </StyledContentWrapper>
      {action && <div>{action}</div>}
    </StyledItem>
  );
}

NotificationItemRoot.displayName = 'NotificationItem';

/* ─────────────────────────── Export (Compound) ───────────────────── */

export const NotificationItem = Object.assign(NotificationItemRoot, {
  Delete: NotificationItemDelete,
  MarkRead: NotificationItemMarkRead,
  Pin: NotificationItemPin,
  Archive: NotificationItemArchive,
  Avatar: NotificationItemAvatar,
  Actions: NotificationItemActions,
});
